import {
  faChevronRight,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { API_URL } from "../../config";
import axios from "axios";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const PickupList = () => {
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSub, setCurrentSub] = useState({ id: null, name: "" });

  const [selectedPickup, setSelectedPickup] = useState(null);
  const [subList, setSubList] = useState([]);

  const fetchSubList = async (pickupId) => {
    try {
      setSubLoading(true);
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/pickuplist/${pickupId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSubList(response.data.data); // Adjust this path if the API structure differs
    } catch (error) {
      setError(error.message);
    } finally {
      setSubLoading(false);
      setLoading(false);
    }
  };

  const fetchPickups = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}settings/pickuplist?pickup_type=crm&limit=100&page=${pageNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const pickups = response.data.data.data;

      setPickups(pickups); // Assuming the API returns an array of users
      if (pickups.length > 0) {
        setSelectedPickup(pickups[0]); // ✅ select first pickup
        fetchSubList(pickups[0].id); // ✅ load its sub list
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users from API using Axios
  useEffect(() => {
    fetchPickups();
  }, []);

  // Modal state and form data
  const [isModalOpenPickupSub, setIsModalOpenPickupSub] = useState(false);
  const [isModalOpenMaster, setIsModalOpenMaster] = useState(false);
  const [masterForm, setMasterForm] = useState({ id: null, pickup_name: "", pickup_type: "crm" });
  const [isMasterEditMode, setIsMasterEditMode] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const openMasterModal = (pickup = null) => {
    if (pickup) {
      setMasterForm({ id: pickup.id, pickup_name: pickup.pickup_name, pickup_type: pickup.pickup_type || "crm" });
      setIsMasterEditMode(true);
    } else {
      setMasterForm({ id: null, pickup_name: "", pickup_type: "crm" });
      setIsMasterEditMode(false);
    }
    setIsModalOpenMaster(true);
  };

  const closeMasterModal = () => {
    setIsModalOpenMaster(false);
    setMasterForm({ id: null, pickup_name: "", pickup_type: "crm" });
    setIsMasterEditMode(false);
  };

  const handleMasterSubmit = async (e) => {
    e.preventDefault();
    if (!masterForm.pickup_name.trim()) {
      showToast.error("Category name is required");
      return;
    }

    setIsLoading(true);
    const url = isMasterEditMode
      ? `${API_URL}settings/pickuplist/master/${masterForm.id}`
      : `${API_URL}settings/pickuplist/master`;
    const method = isMasterEditMode ? "put" : "post";

    try {
      const response = await axios[method](
        url,
        {
          pickup_name: masterForm.pickup_name.trim(),
          pickup_type: masterForm.pickup_type || "crm",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success === true) {
        showToast.success(response.data.message);
        closeMasterModal();
        // Refresh master list
        const refreshResp = await axios.get(
          `${API_URL}settings/pickuplist?pickup_type=crm&limit=100&page=1`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const list = refreshResp.data?.data?.data || [];
        setPickups(list);
        const createdOrSelected =
          list.find(
            (p) =>
              p.pickup_name &&
              p.pickup_name.toLowerCase() === masterForm.pickup_name.trim().toLowerCase()
          ) || list[0];

        if (createdOrSelected) {
          setSelectedPickup(createdOrSelected);
          fetchSubList(createdOrSelected.id);
        }
      } else {
        showToast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error saving master pickup:", err);
      showToast.error("Failed to save master category. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Show loading spinner

    const url = isEditMode
      ? `${API_URL}settings/pickuplist/${currentSub.id}`
      : `${API_URL}settings/pickuplist`;

    const method = isEditMode ? "put" : "post";

    const payload = {
      pid: selectedPickup.id,
      pick_name: currentSub.name,
    };

    if (!currentSub.name) {
      showToast.error("Pickup sub name is required");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success === true) {
        showToast.success(response.data.message); // Success message
        closeModal();
        setCurrentSub({ id: null, name: "" });
        setIsEditMode(false);
        fetchSubList(selectedPickup.id);
      } else {
        showToast.error(response.data.message); // Error message
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast.error("An error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub pickup?"))
      return;

    try {
      const response = await axios.delete(
        `${API_URL}settings/pickuplist/pickup-list-sub/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showToast.success(response.data.message);
      fetchSubList(selectedPickup.id); // Refresh list
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpenPickupSub(false);
    setMessage("");
    setMessageType("");
  };

  // Toggle the modal open/close
  const toggleModal = () => {
    setIsModalOpenPickupSub(!isModalOpenPickupSub);
  };

  return (
    <>
      <div className="page-header-fixed page-sidebar-fixed mt-5">
        <div id="content">
          <div className="row pb-2">
            <div className="col-md-6">
              <h4 className="panel-title pull-left">
                {selectedPickup
                  ? "Manage " + selectedPickup.pickup_name
                  : "Manage Pickup Sub List"}
              </h4>
            </div>
            <div className="col-md-6">
              <PermissionWrapper module="settings_pickuplist" action="add">
                <button
                  className="btn btn-default btn-sm pull-right addModel"
                  onClick={toggleModal}
                  disabled={!selectedPickup}
                >
                  {" "}
                  <FontAwesomeIcon icon={faPlus} />{" "}
                  <span id="add-title">
                    {selectedPickup
                      ? "Add " + selectedPickup.pickup_name
                      : "Add Pickup Sub List"}
                  </span>
                </button>
              </PermissionWrapper>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <div className="card">
                {/* <div className="card-header d-flex justify-content-between align-items-center bg-light py-2 px-3">
                  <span className="fw-bold text-muted small text-uppercase">Categories</span>
                  <PermissionWrapper module="settings_pickuplist" action="add">
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={() => openMasterModal()}
                      title="Add New Master Category"
                      style={{ fontSize: "12px", padding: "4px 10px" }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> New Category
                    </button>
                  </PermissionWrapper>
                </div> */}
                <div className="list-group border" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {pickups.length > 0 ? (
                    pickups.map((item) => (
                      <button
                        key={item.id}
                        className={`list-group-item list-group-flush list-group-item-action border-0 ${selectedPickup && selectedPickup.id === item.id ? "active" : ""
                          }`}
                        onClick={() => {
                          setSelectedPickup(item); // store full pickup object
                          fetchSubList(item.id);
                        }}
                        style={{ padding: "12px 20px" }}
                      >
                        {item.pickup_name}
                        <div className="pull-right">
                          <FontAwesomeIcon icon={faChevronRight} />
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-center text-muted small">
                      No categories found. Click "New Category" to create one.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-9">
              <div className="card">
                <div className="card-body border">
                  <div className="table-responsive">
                    <table className="table" id="simple-table">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th width="200px">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subLoading ? (
                          Array.from({ length: 6 }).map((_, index) => (
                            <tr key={index} className="skeleton-row">
                              <td>
                                <div className="skeleton-box"></div>
                              </td>
                              <td>
                                <div className="skeleton-box" style={{ width: "120px" }}></div>
                              </td>
                            </tr>
                          ))
                        ) : subList.length > 0 ? (
                          subList.map((sub, index) => (
                            <tr key={index}>
                              <td>{sub.pick_name}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <PermissionWrapper
                                    module="settings_pickuplist"
                                    action="edit"
                                  >
                                    <a
                                      href="#"
                                      className="btn btn-warning btn-xs"
                                      onClick={() => {
                                        setCurrentSub({
                                          id: sub.id,
                                          name: sub.pick_name,
                                        });
                                        setIsEditMode(true);
                                        toggleModal();
                                      }}
                                      title="Edit"
                                    >
                                      <FontAwesomeIcon icon={faPencil} />
                                    </a>
                                  </PermissionWrapper>
                                  <PermissionWrapper
                                    module="settings_pickuplist"
                                    action="delete"
                                  >
                                    <a
                                      href="#"
                                      className="cancel btn btn-danger btn-xs"
                                      onClick={() => handleDelete(sub.id)}
                                      title="Delete"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </a>
                                  </PermissionWrapper>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              No sub items available.
                            </td>
                          </tr>
                        )}
                      </tbody>

                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop for Sub Modal */}
      {isModalOpenPickupSub && (
        <div
          className="modal-backdrop fade show"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
          }}
        ></div>
      )}

      {/* Modal structure for Sub Item */}
      {isModalOpenPickupSub && (
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              {/* Form */}
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="modal-header justify-content-between py-0">
                  <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                    {selectedPickup
                      ? `${isEditMode ? "Edit" : "Add"} ${selectedPickup.pickup_name}`
                      : "Modal Heading"}
                  </h5>
                  <button
                    type="button"
                    className="btn"
                    style={{ fontSize: "25px", border: "none" }}
                    onClick={closeModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body">
                  {/* Message Box */}
                  {message && (
                    <div
                      className={`col-md-12 alert m-b-10 ${messageType === "success"
                        ? "alert-success"
                        : "alert-danger"
                        }`}
                    >
                      {message}
                    </div>
                  )}
                  <div className="row">
                    <div className="form-group col-12">
                      <label className="col-form-label">Option Name</label>
                      <input
                        className="form-control"
                        placeholder="Enter Option Name"
                        type="text"
                        value={currentSub.name}
                        onChange={(e) =>
                          setCurrentSub({ ...currentSub, name: e.target.value })
                        }
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="submit"
                    className="btn btn-primary mx-2"
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for Master Modal */}
      {isModalOpenMaster && (
        <div
          className="modal-backdrop fade show"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
          }}
        ></div>
      )}

      {/* Modal structure for Master Category */}
      {isModalOpenMaster && (
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="masterModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              {/* Form */}
              <form onSubmit={handleMasterSubmit}>
                <div className="modal-header justify-content-between py-2">
                  <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                    {isMasterEditMode ? "Edit Category" : "New Master Category"}
                  </h5>
                  <button
                    type="button"
                    className="btn"
                    style={{ fontSize: "25px", border: "none" }}
                    onClick={closeMasterModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="form-group col-12">
                      <label className="col-form-label">Category Name</label>
                      <input
                        className="form-control"
                        placeholder="e.g. Price Type"
                        type="text"
                        value={masterForm.pickup_name}
                        onChange={(e) =>
                          setMasterForm({ ...masterForm, pickup_name: e.target.value })
                        }
                        autoFocus
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={closeMasterModal}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm mx-2"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Category"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PickupList;

